// src/components/ShareModal.tsx
// Beautiful modal for creating and managing shares

import React, { useState, useEffect } from 'react';
import { SharedSession } from './types';
import { 
  calculateViewsRemaining,
  calculateDaysRemaining,
  formatTimeRemaining,
  getShareStatus,
  getShareStatusDisplay 
} from '../utils/sharingHelpers';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  shareUrl: string | null;
  shareData: (SharedSession & { videoUrl?: string }) | null;
  onCreateNew: () => void;
  onRevoke: () => void;
  isCreating: boolean;
  videoTitle: string;
}

const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  shareUrl,
  shareData,
  onCreateNew,
  onRevoke,
  isCreating,
  videoTitle
}) => {
  const [copied, setCopied] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setCopied(false);
      setShowRevokeConfirm(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCopyUrl = async () => {
    if (!shareUrl) return;
    
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      alert('Failed to copy link. Please copy manually.');
    }
  };

  const handleRevoke = () => {
    if (!showRevokeConfirm) {
      setShowRevokeConfirm(true);
      return;
    }
    onRevoke();
    setShowRevokeConfirm(false);
  };

  // Calculate share info if we have data
  let viewsRemaining = 0;
  let daysRemaining = 0;
  let timeFormatted = '';
  let status = 'none';
  let statusDisplay = { emoji: '', color: '', text: '', bgColor: '' };
  
  if (shareData) {
    viewsRemaining = calculateViewsRemaining(shareData.accessCount, shareData.maxViews);
    daysRemaining = calculateDaysRemaining(shareData.expiresAt);
    timeFormatted = formatTimeRemaining(shareData.expiresAt);
    status = getShareStatus(shareData);
    statusDisplay = getShareStatusDisplay(status as any);
  }

  const isNearLimit = shareData && (viewsRemaining <= 5 || daysRemaining <= 1);
  const isExpiredOrMaxed = status === 'expired' || status === 'maxed';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #e9ecef'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ margin: '0 0 4px 0', fontSize: '24px', color: '#212529' }}>
                🔗 Share Video
              </h2>
              <p style={{ margin: 0, fontSize: '14px', color: '#6c757d' }}>
                {videoTitle}
              </p>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '28px',
                cursor: 'pointer',
                color: '#6c757d',
                padding: '0',
                lineHeight: '1'
              }}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          {/* No share exists */}
          {!shareUrl && !isCreating && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎬</div>
              <h3 style={{ margin: '0 0 8px 0', color: '#212529' }}>
                Create a Share Link
              </h3>
              <p style={{ margin: '0 0 24px 0', color: '#6c757d' }}>
                Share this video with anyone. Links expire after 7 days or 30 views.
              </p>
              <button
                onClick={onCreateNew}
                style={{
                  padding: '12px 32px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#0056b3'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#007bff'}
              >
                Create Share Link
              </button>
              <div style={{
                marginTop: '20px',
                padding: '12px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                fontSize: '13px',
                color: '#6c757d',
                textAlign: 'left'
              }}>
                <strong>Share limits:</strong>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                  <li>Maximum 30 views per share</li>
                  <li>Expires after 7 days</li>
                  <li>Only 1 active share at a time</li>
                </ul>
              </div>
            </div>
          )}

          {/* Creating share */}
          {isCreating && (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
              <h3 style={{ margin: '0 0 8px 0', color: '#212529' }}>
                Creating Share Link...
              </h3>
              <p style={{ margin: 0, color: '#6c757d' }}>
                Please wait a moment
              </p>
            </div>
          )}

          {/* Share exists - Active */}
          {shareUrl && shareData && !isExpiredOrMaxed && (
            <div>
              {/* Status Badge */}
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '8px 16px',
                borderRadius: '20px',
                backgroundColor: statusDisplay.bgColor,
                marginBottom: '16px'
              }}>
                <span style={{ marginRight: '8px', fontSize: '16px' }}>
                  {statusDisplay.emoji}
                </span>
                <span style={{ 
                  fontWeight: 'bold', 
                  fontSize: '14px',
                  color: statusDisplay.color 
                }}>
                  {statusDisplay.text}
                </span>
              </div>

              {/* Warning if near limit */}
              {isNearLimit && (
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <span style={{ fontSize: '24px' }}>⚠️</span>
                  <div style={{ flex: 1 }}>
                    <strong style={{ display: 'block', color: '#856404', marginBottom: '4px' }}>
                      Near Limit
                    </strong>
                    <span style={{ fontSize: '14px', color: '#856404' }}>
                      {viewsRemaining <= 5 && `Only ${viewsRemaining} views remaining. `}
                      {daysRemaining <= 1 && `Expires ${timeFormatted}.`}
                    </span>
                  </div>
                </div>
              )}

              {/* Share URL */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: 'bold',
                  color: '#495057'
                }}>
                  Share Link
                </label>
                <div style={{
                  display: 'flex',
                  gap: '8px'
                }}>
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    style={{
                      flex: 1,
                      padding: '12px',
                      border: '1px solid #ced4da',
                      borderRadius: '8px',
                      fontSize: '14px',
                      backgroundColor: '#f8f9fa',
                      fontFamily: 'monospace'
                    }}
                    onClick={(e) => e.currentTarget.select()}
                  />
                  <button
                    onClick={handleCopyUrl}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: copied ? '#28a745' : '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      transition: 'background-color 0.2s'
                    }}
                  >
                    {copied ? '✓ Copied!' : '📋 Copy'}
                  </button>
                </div>
              </div>

              {/* Stats */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px',
                marginBottom: '20px'
              }}>
                {/* Views */}
                <div style={{
                  padding: '16px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #dee2e6'
                }}>
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#6c757d',
                    marginBottom: '4px',
                    fontWeight: '600'
                  }}>
                    VIEWS
                  </div>
                  <div style={{ 
                    fontSize: '24px', 
                    fontWeight: 'bold',
                    color: '#212529',
                    marginBottom: '4px'
                  }}>
                    {shareData.accessCount}/{shareData.maxViews}
                  </div>
                  <div style={{
                    width: '100%',
                    height: '6px',
                    backgroundColor: '#e9ecef',
                    borderRadius: '3px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${(shareData.accessCount / shareData.maxViews) * 100}%`,
                      height: '100%',
                      backgroundColor: viewsRemaining <= 5 ? '#dc3545' : '#28a745',
                      transition: 'width 0.3s'
                    }} />
                  </div>
                  <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                    {viewsRemaining} remaining
                  </div>
                </div>

                {/* Expiry */}
                <div style={{
                  padding: '16px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #dee2e6'
                }}>
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#6c757d',
                    marginBottom: '4px',
                    fontWeight: '600'
                  }}>
                    EXPIRES IN
                  </div>
                  <div style={{ 
                    fontSize: '24px', 
                    fontWeight: 'bold',
                    color: '#212529',
                    marginBottom: '8px'
                  }}>
                    {timeFormatted}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6c757d' }}>
                    {shareData.expiresAt instanceof Date 
                      ? shareData.expiresAt.toLocaleDateString()
                      : new Date(shareData.expiresAt).toLocaleDateString()
                    }
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setShowRevokeConfirm(!showRevokeConfirm)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: showRevokeConfirm ? '#dc3545' : 'white',
                    color: showRevokeConfirm ? 'white' : '#dc3545',
                    border: `2px solid #dc3545`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {showRevokeConfirm ? '⚠️ Confirm Revoke?' : '🗑️ Revoke Share'}
                </button>
                {showRevokeConfirm && (
                  <button
                    onClick={handleRevoke}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    Yes, Revoke
                  </button>
                )}
              </div>

              {showRevokeConfirm && (
                <div style={{
                  marginTop: '12px',
                  padding: '12px',
                  backgroundColor: '#f8d7da',
                  border: '1px solid #f5c6cb',
                  borderRadius: '8px',
                  fontSize: '13px',
                  color: '#721c24'
                }}>
                  <strong>Warning:</strong> Revoking will immediately disable this share link. 
                  Anyone with the link will no longer be able to access the video.
                </div>
              )}
            </div>
          )}

          {/* Share expired or maxed */}
          {shareUrl && shareData && isExpiredOrMaxed && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>
                {status === 'expired' ? '⏰' : '👀'}
              </div>
              <h3 style={{ margin: '0 0 8px 0', color: '#212529' }}>
                {status === 'expired' ? 'Share Expired' : 'View Limit Reached'}
              </h3>
              <p style={{ margin: '0 0 24px 0', color: '#6c757d' }}>
                {status === 'expired' 
                  ? 'This share link expired after 7 days.'
                  : 'This share has reached its maximum of 30 views.'
                }
              </p>
              <button
                onClick={onCreateNew}
                disabled={isCreating}
                style={{
                  padding: '12px 32px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: isCreating ? 'not-allowed' : 'pointer',
                  opacity: isCreating ? 0.6 : 1
                }}
              >
                {isCreating ? 'Creating...' : 'Create New Share Link'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareModal;